import Arc from './Arc'
import { colorLerp, colorToStr, lerp, colorLerp3, COLOR } from './Tools'

export default class Scene
{
    constructor(canvas)
    {
        this.canvas = canvas
        this.context = canvas.getContext('2d')
        this.init()
    }

    init()
    {
        this.state = 0
        this.colors = {
            white:  { r: 242, g: 242, b: 242, a: 1 },  // White
            dark: { r: 13,  g: 36,  b: 53,  a: 1 },    // Dark
            blue:  { r: 0,   g: 83,  b: 255, a: 1 },   // Blue
            black: { r: 0,   g: 0,   b: 0,   a: 1 }    // Black
        }

        this.list = []
        const max = 8
        for (let i = 0; i < max; i++)
        {
            const ext = (i + 1) / max
            const options = {
                radius: 1 / 4 + ext * 3 / 4,
                rotation: Math.random() * Math.PI * 2,
                angle: Math.PI / 4,
                weight: 6 + ext * 10 + Math.random() * 60
            }

            const arc = new Arc(options)
            this.list.push(arc)
        }
    }

    resize(width, height)
    {
        this.width = width
        this.height = height
        this.canvas.width = width
        this.canvas.height = height

        if (this._saveRender)
        {
            this.render(...this._saveRender)
        }
    }

    render(fromState, toState, time, id)
    {
        this._saveRender = [fromState, toState, time, id]
        this._renderBackground(fromState, toState, time)
        this._renderArcs(fromState, toState, time, id)
    }

    _renderBackground(from, to, time)
    {
        const diagonal = Math.sqrt(Math.pow(this.width / 2, 2) + Math.pow(this.height / 2, 2))

        const getColor = state =>
        {
            switch (state)
            {
                case Scene.STATE_START : return this.colors.white
                case Scene.STATE_WHITE : return this.colors.white
                case Scene.STATE_DARK : return this.colors.dark
                case Scene.STATE_BLUE : return this.colors.blue
                default : throw new Error('State ' + state + ' not registered')
            }
        }

        const firstColor = getColor(from)
        const secondColor = getColor(to)

        if (secondColor === firstColor)
        {
            this.context.fillStyle = colorToStr(firstColor)
            this.context.fillRect(0, 0, this.width, this.height)
        }
        else if (secondColor === this.colors.white && firstColor === this.colors.dark)
        {
            const bgColor = colorLerp(firstColor, secondColor, time)
            this.context.fillStyle = colorToStr(bgColor)
            this.context.fillRect(0, 0, this.width, this.height)
        }
        else if (secondColor === this.colors.dark && firstColor === this.colors.white)
        {
            const bgColor = colorLerp3(firstColor, this.colors.black, secondColor, time)
            this.context.fillStyle = colorToStr(bgColor)
            this.context.fillRect(0, 0, this.width, this.height)
        }
        else if (secondColor === this.colors.blue || firstColor === this.colors.blue)
        {
            this.context.fillStyle = colorToStr(firstColor)
            this.context.fillRect(0, 0, this.width, this.height)

            this.context.fillStyle = colorToStr(secondColor)
            this.context.beginPath()
            this.context.arc(
                this.width / 2,
                this.height / 2,
                lerp(0, diagonal, Math.max((time - 0.5) * 2, 0)),
                0,
                2 * Math.PI
            )
            this.context.fill()
        }
    }

    _renderArcs(from, to, time, id)
    {
        const diagonal = Math.sqrt(Math.pow(this.width / 2, 2) + Math.pow(this.height / 2, 2))

        const getArcState = state =>
        {
            switch (state)
            {
                case Scene.STATE_START : return Arc.TYPE_START
                case Scene.STATE_WHITE : return Arc.TYPE_STD
                case Scene.STATE_DARK : return Arc.TYPE_STD
                case Scene.STATE_BLUE : return Arc.TYPE_STD
                default : throw new Error('State ' + state + ' not registered')
            }
        }

        const getArcColor = state =>
        {
            switch (state)
            {
                case Scene.STATE_START : return COLOR.WHITE
                case Scene.STATE_WHITE : return COLOR.WHITE
                case Scene.STATE_DARK : return COLOR.DARK
                case Scene.STATE_BLUE : return COLOR.BLUE
                default : throw new Error('State ' + state + ' not registered')
            }
        }

        if (to === Scene.STATE_BLUE)
        {
            let fromArc
            let toArc
            const fromColor = getArcColor(from)
            let toColor
            let modTime

            if (time < 0.5)
            {
                fromArc = getArcState(from)
                toArc = Arc.TYPE_ALIGN
                toColor = fromColor
                modTime = time * 2
            }
            else
            {
                fromArc = Arc.TYPE_ALIGN
                toArc = getArcState(to)
                toColor = getArcColor(to)
                modTime = time * 2 - 1
            }

            for (const arc of this.list)
            {
                this.context.beginPath()
                this.context.arc(
                    this.width / 2,
                    this.height / 2,
                    arc.getRadius() * diagonal,
                    arc.getRotation(fromArc, toArc, modTime, id),
                    arc.getRotation(fromArc, toArc, modTime, id) + arc.getAngle(fromArc, toArc, modTime, id),
                    false
                )
                this.context.strokeStyle = colorToStr(arc.getColor(fromColor, toColor, modTime))
                this.context.lineWidth = arc.getWeight()
                this.context.stroke()
            }
        }
        else
        {
            const fromArc = getArcState(from)
            const toArc = getArcState(to)
            const fromColor = getArcColor(from)
            const toColor = getArcColor(to)

            for (const arc of this.list)
            {
                this.context.beginPath()
                this.context.arc(
                    this.width / 2,
                    this.height / 2,
                    arc.getRadius() * diagonal,
                    arc.getRotation(fromArc, toArc, time, id),
                    arc.getRotation(fromArc, toArc, time, id) + arc.getAngle(fromArc, toArc, time, id),
                    false
                )
                this.context.strokeStyle = colorToStr(arc.getColor(fromColor, toColor, time))
                this.context.lineWidth = arc.getWeight()
                this.context.stroke()
            }
        }
    }
}

Scene.STATE_START = 0
Scene.STATE_WHITE = 1
Scene.STATE_DARK = 2
Scene.STATE_BLUE = 3
